/**
 * Create or edit an automation: pick a template, pick an audience, choose when.
 *
 * Only approved templates and existing audiences can be selected, so most ways of building a
 * broken automation are closed off here rather than surfacing as a failed run later.
 */
import React, { useState } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useListWhatsAppTemplatesQuery,
  useListAudiencesQuery,
  useCreateAutomationMutation,
  useUpdateAutomationMutation,
} from '../../api/campaignsApi';
import MediaPickerField from './MediaPickerField';

/** Header formats that need a file attached to every send. */
const MEDIA_HEADERS = new Set(['Image', 'Video', 'Document']);

const apiError = (e) =>
  e?.data?.error?.message
  || Object.values(e?.data?.error?.errors ?? {}).flat()[0]
  || 'Something went wrong.';

// Every LeadStatus the backend recognises (Healing.Domain.Enums.LeadEnums). Kept in sync manually —
// there's no /enums endpoint, so this mirrors the server list.
const LEAD_STATUSES = [
  'New', 'Contacted', 'FollowUpPending', 'Qualified', 'ConsultationScheduled', 'NoShow',
  'ConsultationCompleted', 'TreatmentStarted', 'ConvertedPatient', 'ClosedLost', 'JunkLead',
  'NotConnected', 'OutOfRadius', 'NotConverted',
];
const LEAD_STATUS_LABEL = { FollowUpPending: 'Follow-up', NotConnected: 'Not Connected' };

const EVENT_TRIGGERS = new Set(['LeadCreated', 'LeadStatusChanged']);

/** datetime-local wants "YYYY-MM-DDTHH:mm" in local time; the API works in UTC. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AutomationModal({ automation, onClose }) {
  const isNew = !automation;

  const { data: templates } = useListWhatsAppTemplatesQuery({ usableOnly: true });
  const { data: audiences } = useListAudiencesQuery();
  const [create, { isLoading: creating }] = useCreateAutomationMutation();
  const [update, { isLoading: updating }] = useUpdateAutomationMutation();

  const [form, setForm] = useState(() => ({
    name: automation?.name ?? '',
    trigger: automation?.trigger ?? 'DailyRecurring',
    audienceId: automation?.audienceId ?? '',
    whatsAppTemplateId: automation?.whatsAppTemplateId ?? '',
    dailyAtIst: automation?.dailyAtIst ?? '10:00',
    scheduledAtLocal: toLocalInput(automation?.scheduledAtUtc),
    maxRecipientsPerRun: automation?.maxRecipientsPerRun ?? '',
    triggerLeadStatus: automation?.triggerLeadStatus ?? 'NotConnected',
    headerMediaAssetId: automation?.headerMediaAssetId ?? null,
  }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isEventTrigger = EVENT_TRIGGERS.has(form.trigger);
  // Event triggers have no audience — the recipient is always the lead that fired the event, so the
  // scope check below is hardcoded to Lead rather than reading a selected audience's kind.
  const effectiveKind = isEventTrigger ? 'Lead' : null;

  const selectedTemplate = (templates ?? []).find((t) => t.id === form.whatsAppTemplateId);
  const selectedAudience = (audiences ?? []).find((a) => a.id === form.audienceId);
  const targetKind = isEventTrigger ? effectiveKind : selectedAudience?.kind;

  // Catch the mismatch before saving: a patient-scoped field can't fill in for a lead and vice
  // versa. The server checks this too when arming the automation — this is the earlier, friendlier
  // version, and it works whether the target is a picked audience or an event's implicit lead.
  const scopeMismatch = selectedTemplate && targetKind
    ? (selectedTemplate.variables ?? [])
      .filter((v) => v.bindingToken)
      .filter((v) => (targetKind === 'Patient' && v.bindingToken.startsWith('lead.'))
        || (targetKind === 'Lead' && v.bindingToken.startsWith('patient.')))
      .map((v) => v.variableName)
    : [];

  const unbound = (selectedTemplate?.variables ?? [])
    .filter((v) => !v.bindingToken && !v.fallbackText)
    .map((v) => v.variableName);

  // A media-header template carries a file on every send. The template's own default covers sends
  // made outside a campaign (e.g. from the inbox), so it only counts as missing when neither exists.
  const mediaKind = MEDIA_HEADERS.has(selectedTemplate?.headerFormat)
    ? selectedTemplate.headerFormat : null;
  const mediaMissing = mediaKind
    && !form.headerMediaAssetId && !selectedTemplate?.defaultHeaderMediaAssetId;

  const save = async () => {
    const body = {
      name: form.name.trim(),
      trigger: form.trigger,
      // Event triggers carry no audience — sending one would be ignored server-side anyway, but
      // omitting it here keeps the request honest about what this automation actually does.
      audienceId: isEventTrigger ? null : form.audienceId,
      whatsAppTemplateId: form.whatsAppTemplateId,
      dailyAtIst: form.trigger === 'DailyRecurring' ? form.dailyAtIst : null,
      scheduledAtUtc: form.trigger === 'OneOffCampaign' && form.scheduledAtLocal
        ? new Date(form.scheduledAtLocal).toISOString()
        : null,
      maxRecipientsPerRun: isEventTrigger ? null
        : (form.maxRecipientsPerRun === '' ? null : Number(form.maxRecipientsPerRun)),
      triggerLeadStatus: form.trigger === 'LeadStatusChanged' ? form.triggerLeadStatus : null,
      // Only meaningful for a media-header template; sending it otherwise would persist a file
      // reference the send path never reads.
      headerMediaAssetId: mediaKind ? form.headerMediaAssetId : null,
    };
    try {
      if (isNew) await create(body).unwrap();
      else await update({ id: automation.id, ...body }).unwrap();
      toast.success(isNew
        ? 'Automation saved. Switch it on when you’re ready for it to send.'
        : 'Automation updated.');
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const canSave = form.name.trim() && form.whatsAppTemplateId && (
    form.trigger === 'DailyRecurring' ? (form.audienceId && form.dailyAtIst)
    : form.trigger === 'OneOffCampaign' ? (form.audienceId && form.scheduledAtLocal)
    : form.trigger === 'LeadStatusChanged' ? !!form.triggerLeadStatus
    : true   // LeadCreated needs nothing further
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'New automation' : form.name}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div>
            <label className="form-label">Name</label>
            <input className="form-control" placeholder="Daily win-back nudge"
              value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </div>

          <div>
            <label className="form-label">Send this template</label>
            <select className="form-control" value={form.whatsAppTemplateId}
              onChange={(e) => set({ whatsAppTemplateId: e.target.value })}>
              <option value="">Choose an approved template…</option>
              {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {(templates ?? []).length === 0 && (
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                No approved, enabled templates yet — create or enable one under{' '}
                <Link to="/communications">Communications → Templates</Link>.
              </div>
            )}
            {selectedTemplate && (
              <div className="td-muted" style={{ fontSize: '0.78rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                {selectedTemplate.bodyText}
              </div>
            )}
          </div>

          {mediaKind && (
            <MediaPickerField
              kind={mediaKind}
              value={form.headerMediaAssetId}
              onChange={(id) => set({ headerMediaAssetId: id })}
            />
          )}

          {!isEventTrigger && (
            <div>
              <label className="form-label">To this audience</label>
              <select className="form-control" value={form.audienceId}
                onChange={(e) => set({ audienceId: e.target.value })}>
                <option value="">Choose an audience…</option>
                {(audiences ?? []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name} ({a.kind})</option>
                ))}
              </select>
            </div>
          )}

          {isEventTrigger && (
            <div className="td-muted" style={{ fontSize: '0.78rem' }}>
              No audience needed — this sends to the one lead that triggers the event, the moment it happens.
            </div>
          )}

          {unbound.length > 0 && (
            <Warn>
              These template variables aren’t linked to a CRM field yet: <strong>{unbound.join(', ')}</strong>.
              Link them on the Templates tab — the automation can’t be switched on until you do.
            </Warn>
          )}
          {mediaMissing && (
            <Warn>
              This template has a media header ({mediaKind.toLowerCase()}), so every message carries a
              file. Choose one above — the automation can’t be switched on until you do.
            </Warn>
          )}
          {scopeMismatch.length > 0 && (
            <Warn>
              This is a {selectedAudience.kind.toLowerCase()} audience, but <strong>{scopeMismatch.join(', ')}</strong> pulls
              from the other type. Those values would come out blank.
            </Warn>
          )}

          <div>
            <label className="form-label">When</label>
            <select className="form-control" value={form.trigger} onChange={(e) => set({ trigger: e.target.value })}>
              <option value="DailyRecurring">Every day</option>
              <option value="OneOffCampaign">Once</option>
              <option value="LeadCreated">When a lead is created</option>
              <option value="LeadStatusChanged">When a lead's status changes to…</option>
            </select>
            {form.trigger === 'LeadCreated' && (
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                Fires the instant a lead is created — from the "New lead" button, converting a call
                log, or a Meta / website form submission. Whichever way it happened.
              </div>
            )}
          </div>

          {form.trigger === 'LeadStatusChanged' && (
            <div>
              <label className="form-label">Fires when status changes to</label>
              <select className="form-control" style={{ width: 240 }} value={form.triggerLeadStatus}
                onChange={(e) => set({ triggerLeadStatus: e.target.value })}>
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>{LEAD_STATUS_LABEL[s] || s}</option>
                ))}
              </select>
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                Only fires on the transition INTO this status — saving the same status again won't re-send it.
              </div>
            </div>
          )}

          {form.trigger === 'DailyRecurring' && (
            <div>
              <label className="form-label">Time of day (IST)</label>
              <input className="form-control" type="time" style={{ width: 160 }}
                value={form.dailyAtIst} onChange={(e) => set({ dailyAtIst: e.target.value })} />
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                Each person is messaged once per day at most, even if they keep matching.
              </div>
            </div>
          )}

          {form.trigger === 'OneOffCampaign' && (
            <div>
              <label className="form-label">Send at</label>
              <input className="form-control" type="datetime-local" style={{ width: 240 }}
                value={form.scheduledAtLocal} onChange={(e) => set({ scheduledAtLocal: e.target.value })} />
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                Fires once, then switches itself off.
              </div>
            </div>
          )}

          {!isEventTrigger && (
            <div>
              <label className="form-label">Cap recipients per run (optional)</label>
              <input className="form-control" type="number" min="1" style={{ width: 160 }}
                placeholder="server default"
                value={form.maxRecipientsPerRun}
                onChange={(e) => set({ maxRecipientsPerRun: e.target.value })} />
              <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 3 }}>
                A safety limit on top of the server-wide cap. The smaller of the two applies.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={creating || updating}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={creating || updating || !canSave}>
            <Save size={15} /> {isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Warn({ children }) {
  return (
    <div className="td-muted" style={{ fontSize: '0.78rem', display: 'flex', gap: 6, color: 'var(--danger)' }}>
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}
